import axios from "../helpers/axios";
import { desiresConstants } from "./constants";
import store from "../store";

const DESIRES_READ_TTL_MS = 15 * 1000;
const desiresReadState = new Map();

const normalizeDesiresSort = (sort) => {
  const value = String(sort || "newest").trim();
  return value || "newest";
};

const invalidateDesiresReadCache = () => {
  desiresReadState.clear();
};

const getDesiresItems = (sort, options = {}) => {
  return async (dispatch, getState) => {
    const normalizedSort = normalizeDesiresSort(sort);
    const force = options?.force === true;
    const { auth: { authenticate } } = store.getState();

    if (!authenticate) {
      const localDesiresItems = JSON.parse(localStorage.getItem("desires")) || [];

      dispatch({
        type: desiresConstants.GET_TO_DESIRES_SUCCESS,
        payload: { desiresItems: localDesiresItems },
      });

      return localDesiresItems;
    }

    const now = Date.now();
    const current = desiresReadState.get(normalizedSort);

    if (!force && current?.promise) {
      return current.promise;
    }

    if (
      !force
      && current?.loadedAt
      && now - current.loadedAt < DESIRES_READ_TTL_MS
    ) {
      return getState?.()?.desires?.desiresItems || [];
    }

    dispatch({ type: desiresConstants.GET_TO_DESIRES_REQUEST });

    const request = (async () => {
      try {
        const res = await axios.post(
          `/user/getDesiresItems?sort=${encodeURIComponent(normalizedSort)}`
        );
        const desiresItems = res.status === 200
          ? (res.data?.desiresItems || [])
          : [];

        dispatch({
          type: desiresConstants.GET_TO_DESIRES_SUCCESS,
          payload: { desiresItems },
        });

        desiresReadState.set(normalizedSort, {
          promise: null,
          loadedAt: Date.now(),
        });

        return desiresItems;
      } catch (error) {
        dispatch({
          type: desiresConstants.GET_TO_DESIRES_FAILURE,
          payload: { error },
        });

        return [];
      }
    })();

    desiresReadState.set(normalizedSort, {
      promise: request,
      loadedAt: current?.loadedAt || 0,
    });

    try {
      return await request;
    } finally {
      const latest = desiresReadState.get(normalizedSort);

      if (latest?.promise === request) {
        desiresReadState.set(normalizedSort, {
          promise: null,
          loadedAt: latest.loadedAt || 0,
        });
      }
    }
  };
};

export const addToDesires = (product) => {
  return async (dispatch) => {
    try {
      const { desires: { desiresItems }, auth } = store.getState();

      desiresItems[product._id] = { ...product };

      if (auth.authenticate) {
        dispatch({ type: desiresConstants.ADD_TO_DESIRES_REQUEST });

        const payload = {
          desiresItems: [{ product: product._id }],
        };
        const res = await axios.post("/user/desires/addToDesires", payload);

        if (res.status === 201) {
          invalidateDesiresReadCache();
          await dispatch(getDesiresItems(undefined, { force: true }));
        }
      } else {
        localStorage.setItem("desires", JSON.stringify(Object.values(desiresItems)));
        dispatch(getDesiresItems());
      }

      dispatch({
        type: desiresConstants.ADD_TO_DESIRES_SUCCESS,
        payload: { desiresItems },
      });
    } catch (error) {
      dispatch({
        type: desiresConstants.ADD_TO_DESIRES_FAILURE,
        payload: { message: error?.response?.data?.message || error.message },
      });
    }
  };
};

export const removeDesiresItem = (payload) => {
  return async (dispatch) => {
    try {
      dispatch({ type: desiresConstants.REMOVE_DESIRES_ITEM_REQUEST });
      const { desires: { desiresItems }, auth: { authenticate } } = store.getState();

      if (authenticate) {
        const res = await axios.post("/user/desires/removeItem", payload);

        if (res.status === 202) {
          dispatch({
            type: desiresConstants.REMOVE_DESIRES_ITEM_SUCCESS,
            payload: res.data
          });

          invalidateDesiresReadCache();
          await dispatch(getDesiresItems(undefined, { force: true }));
        } else {
          dispatch({
            type: desiresConstants.REMOVE_DESIRES_ITEM_FAILURE,
            payload: res.data
          });
        }
      } else if (desiresItems.length > 0) {
        const { productId } = payload;
        const updatedDesiresItems = desiresItems.filter(item => item._id !== productId);
        localStorage.setItem("desires", JSON.stringify(updatedDesiresItems));

        dispatch({
          type: desiresConstants.REMOVE_DESIRES_ITEM_SUCCESS,
        });
        dispatch(getDesiresItems());
      }
    } catch (error) {
      console.log(error);
    }
  };
};

export const updateDesires = () => {
  return async (dispatch) => {
    const { auth } = store.getState();
    const desiresItems = localStorage.getItem("desires")
      ? JSON.parse(localStorage.getItem("desires"))
      : null;

    if (auth.authenticate) {
      localStorage.removeItem("desires");

      if (desiresItems && Object.keys(desiresItems).length > 0) {
        const payload = {
          desiresItems: Object.keys(desiresItems).map((itemKey) => ({
            quantity: desiresItems[itemKey].qty,
            product: desiresItems[itemKey]._id,
          })),
        };

        const res = await axios.post("/user/desires/addToDesires", payload);

        if (res.status === 201) {
          invalidateDesiresReadCache();
          await dispatch(getDesiresItems(undefined, { force: true }));
        }
      } else {
        dispatch(getDesiresItems());
      }

      return;
    }

    if (desiresItems) {
      dispatch({
        type: desiresConstants.ADD_TO_DESIRES_SUCCESS,
        payload: { desiresItems },
      });
      dispatch(getDesiresItems());
    }
  };
};

export { getDesiresItems };
